/**
 * The wire the people-search `filters` URL param carries.
 *
 * The canonical dialect is the proto filter tree, mirroring the backend's
 * protobuf Filter message (services libraries/filters/filters.proto):
 * `{ and | or: [...] }` groups containing leaves such as `{ attribute }`,
 * `{ segment }`, and `{ named_bitmap }`, with negation as a `{ not: ... }`
 * wrapper. Deep links from the Ember app write the same param, so these
 * shapes are a cross-app contract: bytes those links write must decode
 * identically here.
 *
 * The concrete leaf types below describe what this surface's serializers
 * *construct*. What arrives off the URL is user-editable JSON and gets no
 * type-level trust: readers traverse it as {@link JsonValue} and narrow with
 * runtime checks, and anything unrecognized rides through every boundary
 * verbatim.
 */

/**
 * Any value JSON.parse can produce. `undefined` is included only so concrete
 * wire shapes with optional properties stay assignable; JSON.stringify drops
 * undefined-valued keys, so it never reaches the encoded param.
 */
export type JsonValue = string | number | boolean | null | undefined | JsonValue[] | JsonObject;

export type JsonObject = { [key: string]: JsonValue };

/** The value itself when it is a plain JSON object, else null. */
export function asJsonObject(value: JsonValue): JsonObject | null {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) {
    return null;
  }
  return value;
}

export type AttributeFilter = {
  attribute: {
    field: string;
    operator: string;
    value?: string;
    value2?: string;
  };
};

/**
 * Every property optional: the serializer prunes empty and nullish fields,
 * exactly as the wire's other producers do, so a half-built row serializes to
 * a partial leaf rather than empty-string keys.
 */
export type DeviceAttributeFilter = {
  device_attribute: {
    field?: string;
    operator?: string;
    value?: string;
    value2?: string;
  };
};

export type SegmentFilter = {
  segment: {
    id: number | string;
  };
};

export type SegAttrFilter = {
  seg_attr: {
    segment_id: number | string;
    campaign_id: number | string;
  };
};

export type NewsletterRecipientsFilter = {
  newsletter_recipients: {
    id: number | string;
    templateID?: number | string;
  };
};

export type NamedBitmapFilter = {
  named_bitmap: {
    type: number;
    name: string;
  };
};

export type Filter =
  | AttributeFilter
  | DeviceAttributeFilter
  | SegmentFilter
  | SegAttrFilter
  | NewsletterRecipientsFilter
  | NamedBitmapFilter;

export type OrFilter = {
  or: JsonValue[];
};

export type AndFilter = {
  and: JsonValue[];
};

export type NotFilter = {
  not: JsonValue;
};

export type Filters = OrFilter | AndFilter | NotFilter;

/**
 * The legacy message-condition envelope the named-bitmap codec speaks — the
 * decoded form of a `named_bitmap` name. `filters` carries an event's data
 * filters; the bitmap grammar can't encode them, so the codec only ever reads
 * them for the human-flag spelling and the plugins always write `{}`.
 */
export type SerializedLegacyMessageFilter = {
  type: 'message';
  within?: number;
  times?: number;
  inverse: boolean;
  isForeignEvent?: boolean;
  event: {
    name: string;
    type: string;
    filters?: JsonObject;
    human?: boolean;
  };
};
